import {useEffect, useState, useCallback, useContext, useRef, useMemo} from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { CSVLink } from "react-csv";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AuthContext from "../../../auth/AuthContext";
import ReportService from "../../../services/ReportService";
import AlertService from "../../../services/AlertService";
import CourseService from "../../../services/CourseService";
import VMService from "../../../services/VMService";
import ConfirmDialogBox from "../../confirmDialog/ConfirmDialogBox";
import { Dropdown } from "primereact/dropdown";

const BaySessionsReport = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const isInitialMount = useRef(true);

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState(0);
  const abortControllerRef = useRef(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [hours, setHours] = useState("");
  const [minutes, setMinutes] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const params = useParams();
  const { state } = useLocation();
  const routeCourseId =
  state?.id ||
  sessionStorage.getItem("selectedCourseId") ||
  null;
const isCourseRoute = !!params.courseName;
const endSessionControllerRef = useRef(null);
const vmStopControllerRef = useRef(null);
const [showDeleteDialog, setShowDeleteDialog] = useState(false);
const [selectedVm, setSelectedVm] = useState(null);
const [deleting, setDeleting] = useState(false);

const formatDateTime = (dateString) => {
  if (!dateString) return "-";

  try {
    const clean = dateString.split(".")[0] + "Z";

    const date = new Date(clean);

    if (isNaN(date.getTime())) return "-";

    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-IN", { month: "short" });
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");

    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    hours = String(hours).padStart(2, "0");

    return `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
  } catch {
    return "-";
  }
};

  const supportAccess = authContext.getPermissions("report");
  
  useEffect(() => {
    if (
      supportAccess.filter((f) => f.access.includes(1)).length === 0 ||
      authContext.onGoingValidations
    ) {
      navigate("/");
    }
  }, [supportAccess, navigate, authContext]);

const loadCourses = useCallback(async (signal) => { 
    try {
      const data = await CourseService.GetCourses(authContext, signal); 
      if (signal?.aborted) return; 
      if (data.isSuccess) {
        const options = [{ value: 0, label: "All" }, ...data.data];
        setCourses(options);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    }
  }, [authContext]);

const loadResults = useCallback(async (selectedCourseId) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    try {
      const data = await ReportService.getBaySessionReport(
        selectedCourseId,
        authContext,
        controller.signal 
      );

      if (controller.signal.aborted) return; 

      if (data?.isSuccess) {
        setResults(data.data);
        setCurrentPage(0);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return;
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [authContext]);
  
useEffect(() => {
    const controller = new AbortController(); 
    if (!isCourseRoute) loadCourses(controller.signal);
    return () => controller.abort(); 
  }, [loadCourses, isCourseRoute]);


useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }

    if (isCourseRoute && routeCourseId) {
      setCourseId(routeCourseId);
      loadResults(routeCourseId);
    } else {
      setCourseId(0);
      loadResults(0);
    }

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [routeCourseId, loadResults, isCourseRoute]);

const handleConfirmEndSession = async () => {
  if (!hours && !minutes) {
    AlertService.error("Please enter duration");
    return;
  }

  if (endSessionControllerRef.current) {
    endSessionControllerRef.current.abort();
  }
  const controller = new AbortController();
  endSessionControllerRef.current = controller;

  const totalSeconds =
    parseInt(hours || 0) * 3600 + parseInt(minutes || 0) * 60;

  const payload = {
    id: selectedSession.id,
    durationInSeconds: totalSeconds,
  };

  try {
    const response = await ReportService.endBaySession(
      payload,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (response.isSuccess) {
      AlertService.success("Lab Hours Extended Successfully");
      setShowDialog(false);
      setHours("");
      setMinutes("");
      loadResults(courseId);
    } else {
      AlertService.error(response.errorMessage);
    }
  } catch (err) {
    if (err.name === "AbortError") return;
    AlertService.error("An error occurred");
  }
};

const handleVmStop = async (rowData) => {
  if (vmStopControllerRef.current) {
    vmStopControllerRef.current.abort();
  }
  const controller = new AbortController();
  vmStopControllerRef.current = controller;

  try {
    const response = await VMService.stopVM(
      rowData.vmId,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (response.isSuccess) {
      AlertService.success("VM stopped successfully.");
      loadResults(courseId);
    } else {
      AlertService.error(response.errorMessage);
    }
  } catch (err) {
    if (err.name === "AbortError") return;
    AlertService.error("An error occurred while stopping VM");
  }
};

useEffect(() => {
  return () => {
    endSessionControllerRef.current?.abort();
    vmStopControllerRef.current?.abort();
  };
}, []);

  const skeletonCell = () => (
    <Skeleton width="100%" height="1.5rem" />
  );

  const actionBody = (rowData) => {
    if (loading) return skeletonCell();

    return (
      <div className="d-flex gap-2">
        <button
          className="btn btn-sm btn-danger"
          onClick={() => {
            setSelectedSession(rowData);
            setShowDialog(true);
          }}
        >
          End Session
        </button>

        <button
          className="btn btn-sm btn-warning"
          onClick={() => handleVmStop(rowData)}
        >
          VM Stop
        </button>

        <button
  className="btn btn-sm btn-outline-danger"
  onClick={() => {
    setSelectedVm(rowData);
    setShowDeleteDialog(true);
  }}
>
  Delete
</button>
      </div>
    );
  };
  const paginatedResults = useMemo(() => {
  const startIndex = currentPage * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  if (loading) {
    return Array.from({ length: rowsPerPage }, (_, i) => ({ id: i }));
  }

  return results.slice(startIndex, endIndex);
}, [results, currentPage, rowsPerPage, loading]);

const generatePageNumbers = () => {
const totalPages = Math.max(
  1,
  Math.ceil(results.length / rowsPerPage)
);
  const currentPageNumber = currentPage + 1;
  const pages = [];
  const maxVisiblePages = 3;

  if (totalPages <= 5) {
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(1);
    if (currentPageNumber > maxVisiblePages + 1) pages.push("...");

    let start = Math.max(2, currentPageNumber - 1);
    let end = Math.min(totalPages - 1, currentPageNumber + 1);

    if (currentPageNumber <= maxVisiblePages) end = 4;
    else if (currentPageNumber > totalPages - maxVisiblePages)
      start = totalPages - 3;

    for (let i = start; i <= end; i++) {
      if (i > 1 && i < totalPages) pages.push(i);
    }

    if (currentPageNumber < totalPages - maxVisiblePages) pages.push("...");
    pages.push(totalPages);
  }
  return pages;
};

const handleDelete = async () => {
  if (!selectedVm || deleting) return; 

  setDeleting(true);

  try {
    const response = await VMService.RemoveSession(
      selectedVm.id,
      authContext
    );

    if (response.isSuccess) {
      AlertService.success("Deleted successfully");
      loadResults(courseId);
    } else {
      AlertService.error(response.errorMessage);
    }
  } catch (err) {
    AlertService.error("Error while deleting");
  } finally {
    setDeleting(false);
    setShowDeleteDialog(false);
    setSelectedVm(null);
  }
};

  return (
    <div className="container-fluid custom-container position-relative mb-5 mt-2">

      {/* Filter + Export */}
      <div className="d-flex justify-content-end gap-3 mb-3">
        {!isCourseRoute && (
<Dropdown
    value={courseId}
    onChange={(e) => {
      const value = Number(e.value);
      setCourseId(value);
      loadResults(value);
    }}
    options={courses}
    optionLabel="label"
    optionValue="value"
    placeholder="Select a Course"
    filter
    filterPlaceholder="Search course..."
    disabled={loading}
    className="custom-input-color report-dropdown"
    panelClassName="report-dropdown-panel"
    emptyMessage="No courses found"
    emptyFilterMessage="No matching course found"
    style={{ minWidth: "220px", maxWidth: "300px" }}
  />
        )}

        <CSVLink
          data={results}
          filename={"Bay-Sessions-Report.csv"}
          className="btn btn-primary"
        >
     <i className="pi pi-upload me-2"></i>Export
        </CSVLink>
      </div>

      {/* DataTable */}
      <DataTable
      value={paginatedResults}
        className="custom-table"
        emptyMessage={loading ? null : "No Active Sessions Found"}
        responsiveLayout="scroll"
      >
        <Column
          header="S.No"
          body={
            loading
              ? () => skeletonCell()
              : (_rowData, { rowIndex }) =>
    currentPage * rowsPerPage + rowIndex + 1
          }
          style={{ width: "80px", textAlign: "center" }}
        />

        <Column
          field="vmId"
          header="VM ID"
          body={(rowData) =>
            loading ? skeletonCell() : rowData.vmId
          }
        />

        <Column
          field="userName"
          header="User Email"
          body={(rowData) =>
            loading ? skeletonCell() : rowData.userName
          }
        />

        <Column
          field="displayName"
          header="Display Name"
          body={(rowData) =>
            loading ? skeletonCell() : rowData.displayName
          }
        />

        <Column
          field="courseName"
          header="Course Name"
          body={(rowData) =>
            loading ? skeletonCell() : rowData.courseName
          }
        />

        <Column
          field="startDateTime"
          header="Start Date Time"
          body={(rowData) =>
            loading
              ? skeletonCell()
              : formatDateTime(rowData.startDateTime)
          }
        />


        <Column header="Action" body={actionBody} />
      </DataTable>
      <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-center mt-3 gap-2 paginator-wrapper">

  <div className="d-flex align-items-center">
    <label className="me-2 fw-bold">Show Rows:</label>
    <select
      className="form-select form-select-sm w-auto py-2 text-secondary custom-input-color"
      value={rowsPerPage}
      onChange={(e) => {
        setRowsPerPage(parseInt(e.target.value));
        setCurrentPage(0);
      }}
    >
      {[10, 20, 50].map((size) => (
        <option key={size} value={size}>
          {size}
        </option>
      ))}
    </select>
  </div>

  <div className="d-flex justify-content-center align-items-center gap-2">
    <button
      className="btn p-0 fs-5 pagination-item"
      style={{ width: "40px", height: "40px" }}
      onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
      disabled={currentPage === 0}
    >
      &lt;
    </button>

    {generatePageNumbers().map((page, index) =>
      page === "..." ? (
        <span key={index} className="mx-1 text-secondary">
          ...
        </span>
      ) : (
        <button
          key={index}
          className={`btn p-0 pagination-item ${
            currentPage + 1 === page ? "active-page" : ""
          }`}
          style={{ width: "40px", height: "40px" }}
          onClick={() => setCurrentPage(page - 1)}
        >
          {page}
        </button>
      )
    )}

    <button
      className="btn p-0 fs-5 pagination-item"
      style={{ width: "40px", height: "40px" }}
      onClick={() =>
        setCurrentPage((prev) =>
          Math.min(
            Math.ceil(results.length / rowsPerPage) - 1,
            prev + 1
          )
        )
      }
      disabled={
        currentPage >= Math.ceil(results.length / rowsPerPage) - 1
      }
    >
      &gt;
    </button>
  </div>
</div>

      {/* End Session Dialog */}
      <Dialog
        header="End Bay Session"
        visible={showDialog}
        style={{ width: "400px" }}
        modal
        onHide={() => setShowDialog(false)}
        footer={
          <div className="d-flex justify-content-end gap-2">
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => setShowDialog(false)}
            />
            <Button
              label="Confirm"
              className="p-button-danger"
              onClick={handleConfirmEndSession}
            />
          </div>
        }
      >
        <div className="d-flex gap-3">
          <div className="w-50">
            <label>Hours</label>
            <input
              type="number"
              className="form-control"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder="0"
              min="0"
            />
          </div>
          <div className="w-50">
            <label>Minutes</label>
            <input
              type="number"
              className="form-control"
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              placeholder="0"
              min="0"
              max="59"
            />
          </div>
        </div>
      </Dialog>
<ConfirmDialogBox
  visible={showDeleteDialog}
  onHide={() => setShowDeleteDialog(false)}
  onConfirm={handleDelete}
  title={
    <>
      Are you sure you want to delete this VM{" "}
      <strong>{selectedVm?.vmId}</strong>session data?
    </>
  }
  confirmLabel="Yes, Delete"
  cancelLabel="Cancel"
  confirmDisabled={deleting} 
/>
    </div>

    
  );
};

export default BaySessionsReport;